<template>
	<div
		ref="wysiwyg-wrapper"
		class="flow-text-section"
		:contenteditable="editable"
		:highlight="highlight"
		:highlightState="highlightState"
		:style="{ webkitLineClamp: noOfLines }"
		@blur="updateValue()"
		@keypress="handleKeypress($event)"
		@click="handleClick($event)"
		@keyup="handleKeyup($event)"
		@paste="handlePaste($event)"
		@focus="handleFocus($event)"
	>
		<!--eslint-disable-next-line vue/no-v-html-->
		<div v-if="encodeHTML" ref="text-container" :class="'text-editor ' + textClasses">
			{{ innerValue }}
		</div>
		<div v-else ref="text-container" :class="'text-editor ' + textClasses" v-html="innerValue" />
	</div>
</template>
<script>
import { defineComponent } from "vue";

export default defineComponent({
	name: "WYSIWYG",
	props: {
		/**
		 * value to display
		 */
		value: {
			type: String,
			default: null
		},
		/**
		 * This triggers blur event when enter button pressed
		 */
		removeFocusOnEnter: {
			type: Boolean,
			default: true
		},
		/**
		 * if set it to false then it becomes readonly
		 */
		editable: {
			type: Boolean,
			default: true
		},
		/**
		 * No of lines to display in non-edit mode
		 */
		noOfLines: {
			type: Number,
			default: 3
		},
		/**
		 * true | false
		 */
		highlight: {
			type: Boolean,
			default: false
		},
		/**
		 * primary | success | error | warning
		 */
		highlightState: {
			type: String,
			default: "primary"
		},
		/** space separated classes to apply on text
		 */
		textClasses: {
			type: String,
			default: ""
		},
		/**
		 * handle XSS attack
		 */
		encodeHTML: {
			type: Boolean,
			default: true
		}
	},
	computed: {
		innerValue: {
			// getter
			get() {
				return this.value;
			},
			// setter
			set(newValue) {
				/**
				 * input event
				 *
				 * @event input
				 */
				this.$emit("input", newValue);
				/**
				 * change event
				 *
				 * @event change
				 */
				this.$emit("change", newValue);
				/**
				 * Whenever value is updated.<br/>
				 * use 'value.sync' in parent component to achieve 2 way binding.
				 *
				 * @event update:value
				 */
				this.$emit("update:value", newValue);
			}
		}
	},
	methods: {
		getContent() {
			return this.$refs["text-container"].textContent;
		},
		updateValue() {
			this.innerValue = this.getContent();
			/**
			 * blur event
			 *
			 * @event blur
			 */
			this.$emit("blur", this.innerValue);
			// console.log(this.innerValue);
		},
		// handleInput() {
		// 	this.innerValue = this.getContent();
		// 	/**
		// 	 * input event
		// 	 *
		// 	 * @event input
		// 	 */
		// 	this.$emit("input", this.getContent());
		// 	// console.log(this.innerValue);
		// },
		handleKeypress(e) {
			// /**
			//  * blur event emitted when focus is removed from TextSection
			//  *
			//  * @event blur
			//  */
			// this.$emit("blur", e);
			// console.log(e.keyCode);
			if (e.keyCode === 13 && !e.shiftKey) {
				if (this.removeFocusOnEnter) {
					this.$refs["wysiwyg-wrapper"].blur();
				}
			}

			this.$emit("keypress", e, this.getContent());
		},
		handleClick(e) {
			/**
			 * click event
			 *
			 * @event click
			 */
			this.$emit("click", e, this.getContent());
		},
		handleBlur(e) {
			/**
			 * blur event
			 *
			 * @event click
			 */
			this.$emit("blur", e, this.getContent());
		},
		handleKeyup(e) {
			/**
			 * keyup event
			 *
			 * @event keyup
			 */
			this.$emit("keyup", e, this.getContent());
		},
		handlePaste(e) {
			this.updateValue();
			/**
			 * paste event when something is pasted
			 *
			 * @event paste
			 */
			this.$emit("paste", e, this.getContent());
		},
		handleFocus(e) {
			/**
			 * Focus event
			 *
			 * @event focus
			 */
			this.$emit("focus", e, this.getContent());
		}
	}
});
</script>
<style lang="scss">
div.flow-text-section {
	// display: flex;
	position: relative;
	font-size: 14px;
	min-width: 50px;
	min-height: 12px;
	word-break: break-all;
	&:not([contenteditable="true"]) {
		display: -webkit-box;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	&[contenteditable="true"] {
		&:focus,
		&[highlight],
		&:hover {
			outline: none;

			&:before {
				position: absolute;
				content: "";
				top: -8px;
				left: -8px;
				bottom: -8px;
				right: -8px;
				pointer-events: none;
				border-radius: 4px;
			}
		}
	}

	> .text-editor {
		min-height: 22px;
	}
}

@mixin text-section($text-color, $focus-border, $hover-border, $highlight-states) {
	div.flow-text-section {
		color: $text-color;
		&:focus {
			&:before {
				border: 1px solid $focus-border;
			}
		}
		&[highlight] {
			&:before {
				border: 1px solid $focus-border;
			}

			@each $state, $color in $highlight-states {
				&[highlightState="#{$state}"] {
					&:before {
						border: 1px solid $color;
					}
				}
			}
		}
		&[contenteditable="true"] {
			&:hover:not(:focus):not([highlight]) {
				cursor: text;
				&:before {
					border: 1px solid $hover-border;
				}
			}
		}
	}
}
$d-highlight-states: (
	"primary": var(--dashboard-primary),
	"success": var(--dashboard-success),
	"error": var(--dashboard-danger),
	"warning": var(--dashboard-warning)
);
$s-highlight-states: (
	"primary": var(--slideout-primary),
	"success": var(--slideout-success),
	"error": var(--slideout-danger),
	"warning": var(--slideout-warning)
);

@include text-section(
	var(--dashboard-font),
	var(--dashboard-primary),
	var(--dashboard-border),
	$d-highlight-states
);

.sideout-container {
	@include text-section(
		var(--slideout-font),
		var(--slideout-primary),
		var(--slideout-border),
		$s-highlight-states
	);
}
</style>
